import React, { useState, useMemo } from 'react';
import { FmeaItem } from '../types';
import { useAppContext } from '../contexts/AppContext';
import { Shield, Search, FileText, ArrowUp, ArrowDown, Settings2 } from 'lucide-react';
import { getStandardConfig } from '../constants/fmeaStandards';
import { getRiskLevel } from '../utils/riskCalculators';
import SmartCell from './SmartCell';
import { LayoutSettingsPanel, LayoutSettings, DEFAULT_LAYOUT_SETTINGS, ColorMode } from '../pages/FmeaEditor/LayoutSettingsPanel';

interface FmeaTableProps {
    title: string;
    items: FmeaItem[];
}

type SortDirection = 'ascending' | 'descending' | null;

// Columns that stay compact regardless of wrapText setting
const NO_WRAP_COLS = new Set(['id', 'S', 'O', 'D', 'actionPriority', 'specialCharacteristic', 'RPN']);

const ControlChip: React.FC<{ controlId: string }> = ({ controlId }) => {
    const { allData } = useAppContext();
    const control = allData.controls.find(c => c.id === controlId);
    if (!control) return null;
    const isPrev = control.type === 'prevention';
    return (
        <span
            title={control.name}
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${isPrev
                ? 'bg-chip-prev-bg text-chip-prev border-chip-prev'
                : 'bg-chip-det-bg text-chip-det border-chip-det'
                }`}
        >
            {isPrev ? <Shield size={12} /> : <Search size={12} />}
            {control.id}
        </span>
    );
};

const FmeaTable: React.FC<FmeaTableProps> = ({ title, items }) => {
    const { highlightedItem, navigate, allData } = useAppContext();
    const [sortConfig, setSortConfig] = useState<{ key: keyof FmeaItem | null; direction: SortDirection }>({ key: null, direction: null });
    const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>({
        ...DEFAULT_LAYOUT_SETTINGS,
        wrapText: true,
        colorMode: 'risk-traffic-light',
    });
    const [layoutPanelOpen, setLayoutPanelOpen] = useState(false);

    const standardConfig = useMemo(() => getStandardConfig(allData.projectMetadata?.fmeaStandard), [allData.projectMetadata?.fmeaStandard]);

    const dynamicColumns = useMemo(() => standardConfig.columns.map(c => ({
        label: c.header, key: c.key as keyof FmeaItem, isNumeric: c.isNumeric
    })), [standardConfig]);

    const handleSort = (key: keyof FmeaItem) => {
        let direction: SortDirection = 'ascending';
        if (sortConfig.key === key) {
            if (sortConfig.direction === 'ascending') direction = 'descending';
            else if (sortConfig.direction === 'descending') direction = null;
        }
        setSortConfig({ key: direction ? key : null, direction });
    };

    const sortedItems = useMemo(() => {
        const arr = [...items];
        if (sortConfig.key && sortConfig.direction) {
            arr.sort((a, b) => {
                const av = a[sortConfig.key!], bv = b[sortConfig.key!];
                if (av === bv) return 0;
                if (Array.isArray(av) && Array.isArray(bv)) {
                    return sortConfig.direction === 'ascending' ? av.join().localeCompare(bv.join()) : bv.join().localeCompare(av.join());
                }
                return sortConfig.direction === 'ascending' ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1);
            });
        } else {
            arr.sort((a, b) => b.RPN - a.RPN);
        }
        return arr;
    }, [items, sortConfig]);

    const getRowBg = (item: FmeaItem, idx: number): string => {
        const mode = layoutSettings.colorMode as ColorMode;
        if (mode === 'risk-traffic-light') {
            const standard = allData.projectMetadata?.fmeaStandard || 'AIAG_VDA';
            const level = getRiskLevel(standard, item.S, item.O, item.D);
            if (level === 'critical') return 'bg-red-50 dark:bg-red-900/10';
            if (level === 'high') return 'bg-red-50/60 dark:bg-red-900/5';
            if (level === 'medium') return 'bg-yellow-50 dark:bg-yellow-900/10';
            return 'bg-emerald-50/30 dark:bg-emerald-900/5';
        }
        if (mode === 'alt-gray') return idx % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/40';
        if (mode === 'alt-blue') return idx % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-blue-50/50 dark:bg-blue-900/10';
        if (mode === 'alt-green-white') return idx % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-emerald-50/50 dark:bg-emerald-900/10';
        return '';
    };

    const handleRowClick = (item: FmeaItem) => {
        const test = allData.dvprTests.find(d => d.traceableRiskId?.includes(item.id));
        if (test) navigate('/dvpr', { highlight: { type: 'dvpr', id: test.testId } });
    };

    const hiddenColumns = layoutSettings.hiddenColumns;
    const visibleColumns = dynamicColumns.filter(col => !hiddenColumns.has(col.key as string));
    const showCellBorders = layoutSettings.showCellBorders;

    return (
        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            {/* Compact header bar — just title + Layout button */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-slate-50 dark:bg-slate-900/50">
                <h3 className="text-sm font-bold text-text-primary">{title}</h3>
                <button
                    onClick={() => setLayoutPanelOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-border bg-background text-text-secondary hover:text-primary hover:border-primary/40 transition-all"
                    title="Layout & Display Settings"
                >
                    <Settings2 size={13} />
                    Layout
                </button>
            </div>

            <div className="overflow-x-auto">
                <table
                    className={`w-full text-sm text-left text-text-secondary ${layoutSettings.wrapText ? 'table-auto' : 'whitespace-nowrap table-fixed'}`}
                    style={{ minWidth: 800 }}
                >
                    <thead className="text-[11px] text-text-primary uppercase bg-background/50 sticky top-0 z-10">
                        <tr>
                            {visibleColumns.map(col => (
                                <th
                                    key={col.key as string}
                                    className={`px-3 py-2.5 whitespace-nowrap cursor-pointer hover:bg-background/80 select-none font-bold tracking-wide ${showCellBorders ? 'border border-border/40' : ''} ${col.isNumeric ? 'text-center w-12' : ''}`}
                                    onClick={() => handleSort(col.key)}
                                >
                                    <div className={`flex items-center gap-1 ${col.isNumeric ? 'justify-center' : ''}`}>
                                        {col.label}
                                        {sortConfig.key === col.key && (sortConfig.direction === 'ascending' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                                    </div>
                                </th>
                            ))}
                            {standardConfig.riskModel === 'RPN' && !hiddenColumns.has('_rpn') && (
                                <th className={`px-3 py-2.5 whitespace-nowrap cursor-pointer hover:bg-background/80 text-center font-bold tracking-wide w-14 ${showCellBorders ? 'border border-border/40' : ''}`} onClick={() => handleSort('RPN')}>
                                    <div className="flex items-center gap-1 justify-center">
                                        RPN {sortConfig.key === 'RPN' && (sortConfig.direction === 'ascending' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                                    </div>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                        {sortedItems.map((item, idx) => {
                            const isHighlighted = highlightedItem?.type === 'fmea' && highlightedItem.id === item.id;
                            const hasLink = allData.dvprTests.some(d => d.traceableRiskId?.includes(item.id));
                            const rowBg = getRowBg(item, idx);

                            return (
                                <tr
                                    key={item.id}
                                    className={`transition-colors hover:brightness-95 ${rowBg} ${hasLink ? 'cursor-pointer' : ''} ${isHighlighted ? 'ring-2 ring-primary' : ''}`}
                                    onClick={() => handleRowClick(item)}
                                >
                                    {visibleColumns.map(col => {
                                        const val = item[col.key];
                                        const forceNoWrap = NO_WRAP_COLS.has(col.key as string);
                                        const cellWrap = layoutSettings.wrapText && !forceNoWrap;
                                        const tdCls = `px-3 py-2 ${showCellBorders ? 'border border-border/40' : ''}`;

                                        // ID cell
                                        if (col.key === 'id') return (
                                            <td key="id" className={tdCls}>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="font-mono text-xs text-blue-500 font-bold">{item.id}</span>
                                                    {item.notes && (
                                                        <div className="group relative inline-block">
                                                            <FileText size={12} className="text-blue-400 cursor-help" />
                                                            <div className="absolute left-0 top-full z-50 hidden group-hover:block w-64 bg-black/90 text-white text-xs rounded p-2 shadow-lg mt-1 whitespace-pre-wrap">{item.notes}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        );

                                        // Action Priority badge
                                        if (col.key === 'actionPriority') return (
                                            <td key="ap" className={`${tdCls} text-center`}>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${item.actionPriority === 'H' ? 'bg-red-500 text-white' : item.actionPriority === 'M' ? 'bg-yellow-400 text-black' : 'bg-emerald-500 text-white'}`}>
                                                    {item.actionPriority || 'L'}
                                                </span>
                                            </td>
                                        );

                                        // Controls chips
                                        if ((col.key as string) === 'prevControls' || (col.key as string) === 'detControls') {
                                            const arr = (col.key as string) === 'prevControls' ? item.preventionControls : item.detectionControls;
                                            return (
                                                <td key={col.key as string} className={tdCls}>
                                                    <div className="flex flex-wrap gap-1">
                                                        {(arr || []).map(id => <ControlChip key={id} controlId={id} />)}
                                                    </div>
                                                </td>
                                            );
                                        }

                                        // Special characteristic badge
                                        if ((col.key as string) === 'specialCharacteristic') {
                                            const sc = item.specialCharacteristic;
                                            return (
                                                <td key="sc" className={`${tdCls} text-center`}>
                                                    {sc === 'CC' ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">CC</span>
                                                        : sc === 'SC' ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300">SC</span>
                                                            : <span className="text-xs text-text-secondary">—</span>}
                                                </td>
                                            );
                                        }

                                        // Numeric columns — always compact
                                        if (col.isNumeric) return (
                                            <td key={col.key as string} className={`${tdCls} text-center font-bold`}>
                                                {String(val ?? '')}
                                            </td>
                                        );

                                        // Text columns — SmartCell (full text, scroll if >6 lines)
                                        return (
                                            <td key={col.key as string} className={tdCls}>
                                                <SmartCell value={String(val ?? '')} wrapText={cellWrap} readOnly />
                                            </td>
                                        );
                                    })}

                                    {standardConfig.riskModel === 'RPN' && !hiddenColumns.has('_rpn') && (
                                        <td className={`${showCellBorders ? 'border border-border/40' : ''} px-3 py-2 font-bold text-text-primary text-center`}>{item.RPN}</td>
                                    )}
                                </tr>
                            );
                        })}
                        {sortedItems.length === 0 && (
                            <tr>
                                <td colSpan={visibleColumns.length + 2} className="text-center py-12 text-text-secondary italic text-sm">
                                    No items to display.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Layout Settings Panel (slide-in) */}
            {layoutPanelOpen && (
                <LayoutSettingsPanel
                    settings={layoutSettings}
                    onSettingsChange={setLayoutSettings}
                    onClose={() => setLayoutPanelOpen(false)}
                    availableColumns={standardConfig.columns}
                />
            )}
        </div>
    );
};

export default FmeaTable;
